// Witzzy — the admin surface for the Witzzy-first sequential-fallback stage
// (apps/rtb-api/src/witzzy_dispatch.ts): a send pass posts qualifying
// over-$10K leads to Witzzy's GoHighLevel intake webhook, then a fallback
// pass sells to LeadExec (via the EXISTING lead-sale proxy machinery,
// unchanged) if Witzzy hasn't converted the lead within the configured
// window. Gated: sec-lead-sales (same section as Lead Sales).
//
// Reference: stages/02_prp/output/witzzy_sequential_prp.md (Task 7). Cloned
// from pages/LeadSales.tsx — the SHAPE only (master toggle w/ confirm-to-
// enable, window-picker + 15s poll stats strip, config Card + Save), NOT the
// destination/routing array editors (Witzzy has exactly one destination).
//
// Two data planes, deliberately separate (same split as LeadSales.tsx):
//   - LIVE (stats): windowed, auto-refreshed every 15s, pauses while the tab
//     is hidden. Sends/outcomes/skips come off the witzzy.* Logs Insights
//     group (api.witzzyStats()) — the ledger-backed Witzzy OUTCOME counts
//     (pending/converted/expired/send_failed) live on the Lead Sales
//     dashboard's existing stats instead (routes/lead_sales.ts AC17).
//   - CONFIG (the witzzy_config singleton): loaded once, mutated via the
//     master on/off toggle (the SEND kill switch only — pending fallbacks
//     resolve regardless, gated by the SEPARATE Lead Sales enabled flag) and
//     the config form.
//
// 🔒 Live-safety: turning Witzzy ON starts REAL sends to a live partner, so
// the master toggle requires a deliberate confirm step to enable (Off is
// immediate). The band (wait/max_age) and the conversion/fallback WINDOW PAIR
// carry client-side guards mirroring the server's 400 invalid_band. The
// webhook URL is paste-only: the operator pastes the raw URL, admin-api
// writes it to Secrets Manager and returns only a masked hint (the URL value
// never comes back to this screen). `enabled` is owned by the master toggle,
// never a config Save (a stale form can't re-enable a killed stage).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";

import {
  api,
  ApiError,
  type WitzzyConfig,
  type WitzzyConfigResponse,
  type WitzzyDayKey,
  type WitzzyDayWindow,
  type WitzzyDestination,
  type WitzzyQuotaStatus,
  type WitzzySendWindow,
  type WitzzyStats,
  type WitzzyTier,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const POLL_MS = 15_000;
// Fallback per-tick send ceiling shown before the config (with the real
// tick_cap) loads. sends_per_min may rise to the cap, never past it.
const TICK_CAP_FALLBACK = 60;
// Mirrors routes/witzzy.ts:WITZZY_SYNC_GRACE_MS (5 min) — the minimum
// headroom the fallback max age must leave past the conversion window, so a
// call at the very end of the window is in td-calls before the fallback
// decides. Client-side mirror, in MINUTES, for the live guard below.
const WITZZY_SYNC_GRACE_MIN = 5;

// "Today (PT)" is the default selected window and a special sentinel: it sends
// NO window param so the server applies its Pacific-midnight default. Clone of
// LeadSales.tsx's convention.
const TODAY_PT = -1;

const PRESETS: Array<{ label: string; sec: number }> = [
  { label: "Today (PT)", sec: TODAY_PT },
  { label: "Last 15 min", sec: 15 * 60 },
  { label: "Last 30 min", sec: 30 * 60 },
  { label: "Last hour", sec: 3600 },
  { label: "Last 3 hours", sec: 3 * 3600 },
  { label: "Last 6 hours", sec: 6 * 3600 },
  { label: "Last 24 hours", sec: 24 * 3600 },
  { label: "Last 7 days", sec: 7 * 24 * 3600 },
  { label: "Last 30 days", sec: 30 * 24 * 3600 },
];

const TIER_OPTIONS: Array<{ value: WitzzyTier; label: string }> = [
  { value: "below_10k", label: "Under $10k" },
  { value: "over_10k", label: "$10k–$20k" },
  { value: "over_20k", label: "$20k–$30k" },
  { value: "over_30k", label: "$30k–$40k" },
  { value: "over_40k", label: "$40k+" },
];

// Send window (witzzy_send_window_quota) — Monday-first, mirrors
// apps/rtb-api/src/domain/send_window.ts:WITZZY_DAY_KEYS. Row-label choice
// (the PRP leaves it to the page): full weekday names, used both for the
// visible row label and the "<Day> open"/"opens"/"closes" aria-labels.
const WITZZY_DAY_KEYS: readonly WitzzyDayKey[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];
const DAY_LABELS: Record<WitzzyDayKey, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

type StatsOpts = { windowSec?: number; start?: number; end?: number };

/** Resolve the selected preset/custom into api.witzzyStats opts. Clone of
 *  LeadSales.tsx:toStatsOpts. */
function toStatsOpts(
  presetSec: number,
  custom: { start: string; end: string } | null,
): StatsOpts | undefined {
  if (custom && custom.start && custom.end) {
    const start = Math.floor(new Date(custom.start + "T00:00:00Z").getTime() / 1000);
    const end = Math.floor(new Date(custom.end + "T23:59:59Z").getTime() / 1000);
    return { start, end };
  }
  if (presetSec === TODAY_PT) return undefined; // server default = Pacific Today
  return { windowSec: presetSec };
}

function fmtCount(n: number): string {
  return (n ?? 0).toLocaleString();
}

/** 480 → "08:00". Assumes 0 <= n <= 1439 (the contract's bounds). */
function minutesToHHMM(n: number): string {
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Inverse of minutesToHHMM — null unless v is exactly "HH:MM" with hh < 24
 *  and mm < 60. jsdom and real browsers both sanitise any other
 *  `<input type="time">` value to "" (e.g. a manually-typed "0700" or
 *  "25:00"), which must become a blocked Save, never a NaN on the wire. */
function hhmmToMinutes(v: string): number | null {
  if (!/^\d{2}:\d{2}$/.test(v)) return null;
  const hh = Number(v.slice(0, 2));
  const mm = Number(v.slice(3, 5));
  if (hh >= 24 || mm >= 60) return null;
  return hh * 60 + mm;
}

/** The "Sent today" tile's hint. The tile is ALWAYS today-scoped (in the
 *  configured zone), independent of whatever the range picker above shows —
 *  say so explicitly so the two don't read as the same window. */
function quotaHint(q: WitzzyQuotaStatus): string {
  const scope = `Today ${q.day} (${q.tz})`;
  if (q.window_open) {
    return q.window_today
      ? `${scope} · window open, closes ${minutesToHHMM(q.window_today[1])}`
      : `${scope} · window open`;
  }
  return q.window_today
    ? `${scope} · window closed (${minutesToHHMM(q.window_today[0])}–${minutesToHHMM(q.window_today[1])})`
    : `${scope} · closed today`;
}

// ---------------------------------------------------------------------------
// Config form state. Numeric fields are kept as strings so the inputs can be
// cleared mid-edit; they're parsed + validated on Save. `enabled` is NOT here
// — the master toggle owns it; Save carries the current enabled through.
// ---------------------------------------------------------------------------

interface FormState {
  waitMin: string;
  maxAgeMin: string;
  sendsPerMin: string;
  conversionWindowMin: string;
  fallbackMaxAgeMin: string;
  tiers: WitzzyTier[];
  destinationName: string;
  webhookUrlNew: string;
  tdLeadPostEnabled: boolean;
  tdLeadTokenNew: string;
  // witzzy_call_events — the stream-consumer switch, the attribution window
  // (kept as a string so the input can be cleared mid-edit; parsed 1..30 on
  // Save), and the events webhook paste field (write-only, starts empty).
  eventsEnabled: boolean;
  attributionWindowDays: string;
  eventsUrlNew: string;
  successNumber10: string;
  successTrafficSourceId: string; // blank → null
  // Send window + daily quota (witzzy_send_window_quota). Time fields are
  // kept as "HH:MM" strings (same mid-edit reason as the rest of this form)
  // and round-tripped through hhmmToMinutes on Save.
  tz: string;
  days: Record<WitzzyDayKey, { open: boolean; from: string; to: string }>;
  dailyQuota: string; // blank → unlimited (null)
}

/** Closed-day rows keep the last-edited from/to strings (they're merely
 *  disabled, never cleared); a day that was never open in the stored config
 *  starts at "08:00"/"17:00" so opening it shows sane defaults immediately. */
function dayRowFromWindow(w: WitzzyDayWindow): { open: boolean; from: string; to: string } {
  return w === null
    ? { open: false, from: "08:00", to: "17:00" }
    : { open: true, from: minutesToHHMM(w[0]), to: minutesToHHMM(w[1]) };
}

function configToForm(c: WitzzyConfig): FormState {
  return {
    waitMin: String(c.wait_ms / 60000),
    maxAgeMin: String(c.max_age_ms / 60000),
    sendsPerMin: String(c.sends_per_min),
    conversionWindowMin: String(c.conversion_window_ms / 60000),
    fallbackMaxAgeMin: String(c.fallback_max_age_ms / 60000),
    tiers: [...c.tiers],
    destinationName: c.destination.name,
    webhookUrlNew: "", // paste field starts empty; a new URL is opt-in per Save
    tdLeadPostEnabled: c.td_lead_post_enabled,
    tdLeadTokenNew: "", // paste field starts empty; a new token is opt-in per Save
    eventsEnabled: c.events_enabled,
    attributionWindowDays: String(c.attribution_window_days),
    eventsUrlNew: "", // paste field starts empty; a new URL is opt-in per Save
    successNumber10: c.success_number_10,
    successTrafficSourceId:
      c.success_traffic_source_id === null ? "" : String(c.success_traffic_source_id),
    tz: c.send_window.tz,
    days: {
      mon: dayRowFromWindow(c.send_window.days.mon),
      tue: dayRowFromWindow(c.send_window.days.tue),
      wed: dayRowFromWindow(c.send_window.days.wed),
      thu: dayRowFromWindow(c.send_window.days.thu),
      fri: dayRowFromWindow(c.send_window.days.fri),
      sat: dayRowFromWindow(c.send_window.days.sat),
      sun: dayRowFromWindow(c.send_window.days.sun),
    },
    dailyQuota: c.daily_quota === null ? "" : String(c.daily_quota),
  };
}

/** One day row → a validated WitzzyDayWindow, or an error message. The
 *  single source both sendWindowFormError (the live Save guard) and
 *  buildConfigFromForm read, so the two can never disagree. */
function dayWindowFromRow(
  day: WitzzyDayKey,
  row: { open: boolean; from: string; to: string },
): { window: WitzzyDayWindow } | { error: string } {
  if (!row.open) return { window: null };
  const from = hhmmToMinutes(row.from);
  const to = hhmmToMinutes(row.to);
  if (from === null || to === null) {
    return { error: "Every open day needs a valid opens and closes time (HH:MM)." };
  }
  if (from >= to) {
    return { error: `${DAY_LABELS[day]}: opens must be before closes.` };
  }
  return { window: [from, to] };
}

/** Validates the tz + all seven day rows, Monday-first. Returns the first
 *  error message, or null when the whole window is valid. */
function sendWindowFormError(form: FormState): string | null {
  if (form.tz.trim() === "") return "Time zone is required.";
  for (const d of WITZZY_DAY_KEYS) {
    const result = dayWindowFromRow(d, form.days[d]);
    if ("error" in result) return result.error;
  }
  return null;
}

/** Mirrors the server's daily_quota rule: blank → unlimited (null, no
 *  error), else a whole number >= 1. */
function dailyQuotaFormError(dailyQuota: string): string | null {
  const raw = dailyQuota.trim();
  if (raw === "") return null;
  const q = Number(raw);
  if (!Number.isInteger(q) || q < 1) {
    return "Daily quota must be a whole number of at least 1, or blank for unlimited.";
  }
  return null;
}

// witzzy_call_events — shared between buildConfigFromForm's client guard and
// friendlyConfigError's `invalid_events` mapping below, so the two copies can
// never drift apart. The client only checks the days value (the events URL's
// https:// requirement is server-validated only, AC10) but the message
// mentions both so the operator sees one consistent reason either way.
const INVALID_EVENTS_COPY =
  "The call-events settings are invalid — the window must be a whole number of days from 1 to 30 and the URL must start with https://. If this page has been open a while, reload and try again.";

/** Parse + validate the form into a clean WitzzyConfig, or return a message.
 *  `enabled` is carried through (the master toggle owns it). Mirrors the
 *  server's guards so most 400s are prevented client-side. */
function buildConfigFromForm(
  form: FormState,
  enabled: boolean,
  tickCap: number,
): { config: WitzzyConfig } | { error: string } {
  const wMin = Number(form.waitMin);
  const aMin = Number(form.maxAgeMin);
  if (form.waitMin.trim() === "" || !Number.isFinite(wMin) || wMin <= 0) {
    return { error: "Wait must be a positive number of minutes." };
  }
  if (form.maxAgeMin.trim() === "" || !Number.isFinite(aMin) || aMin <= 0) {
    return { error: "Max age must be a positive number of minutes." };
  }
  const wait_ms = Math.round(wMin * 60000);
  const max_age_ms = Math.round(aMin * 60000);
  if (max_age_ms <= wait_ms) {
    return {
      error:
        "Max age must be greater than wait — otherwise the send-pass eligibility band is empty.",
    };
  }

  const spm = Number(form.sendsPerMin);
  if (form.sendsPerMin.trim() === "" || !Number.isFinite(spm) || spm <= 0) {
    return { error: "Sends/min must be a positive number." };
  }
  if (spm > tickCap) {
    return { error: `Sends/min is capped at ${tickCap}.` };
  }

  const cwMin = Number(form.conversionWindowMin);
  if (
    form.conversionWindowMin.trim() === "" ||
    !Number.isFinite(cwMin) ||
    cwMin <= 0
  ) {
    return { error: "Conversion window must be a positive number of minutes." };
  }
  const conversion_window_ms = Math.round(cwMin * 60000);

  const faMin = Number(form.fallbackMaxAgeMin);
  if (
    form.fallbackMaxAgeMin.trim() === "" ||
    !Number.isFinite(faMin) ||
    faMin <= 0
  ) {
    return { error: "Fallback max age must be a positive number of minutes." };
  }
  const fallback_max_age_ms = Math.round(faMin * 60000);

  if (faMin <= cwMin + WITZZY_SYNC_GRACE_MIN) {
    return {
      error: `Fallback max age must exceed the conversion window by more than ${WITZZY_SYNC_GRACE_MIN} minutes (the td-calls sync grace) — otherwise a call at the very end of the window can be missed.`,
    };
  }

  if (form.tiers.length === 0) {
    return { error: "Pick at least one tier for Witzzy to cover." };
  }

  const name = form.destinationName.trim();
  if (name === "") {
    return { error: "Destination name is required." };
  }

  if (!/^\d{10}$/.test(form.successNumber10.trim())) {
    return { error: "Success number must be exactly 10 digits." };
  }

  let success_traffic_source_id: number | null = null;
  const rawTs = form.successTrafficSourceId.trim();
  if (rawTs !== "") {
    const n = Number(rawTs);
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
      return { error: "Traffic source id must be a whole number, or left blank." };
    }
    success_traffic_source_id = n;
  }

  // The PUT body's destination is `.strict()`-validated server-side to EXACTLY
  // {name, webhook_url_new?} — the ARN is server-authoritative and never
  // round-tripped (see api.ts:WitzzyDestination's doc comment).
  const destination: WitzzyDestination = { name };
  const pastedUrl = form.webhookUrlNew.trim();
  if (pastedUrl !== "") destination.webhook_url_new = pastedUrl;
  const pastedToken = form.tdLeadTokenNew.trim();
  if (pastedToken !== "") destination.td_lead_token_new = pastedToken;
  const pastedEventsUrl = form.eventsUrlNew.trim();
  if (pastedEventsUrl !== "") destination.events_url_new = pastedEventsUrl;

  // Send window + daily quota (witzzy_send_window_quota) — pass-level,
  // transient guards on the send pass; see api.ts:WitzzySendWindow.
  const sendWindowError = sendWindowFormError(form);
  if (sendWindowError) return { error: sendWindowError };
  const windowOf = (d: WitzzyDayKey): WitzzyDayWindow => {
    const r = dayWindowFromRow(d, form.days[d]);
    return "window" in r ? r.window : null; // unreachable: validated above
  };
  const send_window: WitzzySendWindow = {
    tz: form.tz.trim(),
    days: {
      mon: windowOf("mon"),
      tue: windowOf("tue"),
      wed: windowOf("wed"),
      thu: windowOf("thu"),
      fri: windowOf("fri"),
      sat: windowOf("sat"),
      sun: windowOf("sun"),
    },
  };

  const quotaError = dailyQuotaFormError(form.dailyQuota);
  if (quotaError) return { error: quotaError };
  const rawQuota = form.dailyQuota.trim();
  const daily_quota: number | null = rawQuota === "" ? null : Number(rawQuota);

  // witzzy_call_events — client-side guard in the Save-body builder: the
  // attribution window must be a whole integer of days, 1..30 (server
  // default 7). The pasted events URL's https:// requirement is validated
  // server-side only (AC10) — see INVALID_EVENTS_COPY above.
  const attributionWindowDays = Number(form.attributionWindowDays);
  if (
    form.attributionWindowDays.trim() === "" ||
    !Number.isInteger(attributionWindowDays) ||
    attributionWindowDays < 1 ||
    attributionWindowDays > 30
  ) {
    return { error: INVALID_EVENTS_COPY };
  }

  return {
    config: {
      enabled,
      td_lead_post_enabled: form.tdLeadPostEnabled,
      wait_ms,
      max_age_ms,
      sends_per_min: spm,
      conversion_window_ms,
      fallback_max_age_ms,
      tiers: [...form.tiers],
      destination,
      success_number_10: form.successNumber10.trim(),
      success_traffic_source_id,
      send_window,
      daily_quota,
      events_enabled: form.eventsEnabled,
      attribution_window_days: attributionWindowDays,
    },
  };
}

/** Map a config-save failure to a friendly message. The server surfaces
 *  invalid_band / destination_missing_url / invalid_body as the ApiError
 *  `error` code; fall back to the raw message otherwise. Mirrors
 *  LeadSales.tsx:friendlyConfigError. */
function friendlyConfigError(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.body && typeof err.body === "object" && "error" in err.body
        ? (err.body as { error?: unknown }).error
        : undefined;
    if (code === "invalid_band") {
      return "The band or the conversion/fallback window pair is invalid as entered — check the hints below each field.";
    }
    if (code === "destination_missing_url") {
      return "Witzzy needs a webhook URL — paste one below and Save again.";
    }
    if (code === "invalid_send_window") {
      return "The send window is invalid as entered — every open day needs opens before closes and the time zone must be a valid IANA name. If this page has been open a while, reload and try again.";
    }
    if (code === "invalid_td_lead_post") {
      return "The TrackDrive lead-post settings are invalid — the token must be 8–128 letters, digits, '-' or '_'. If this page has been open a while, reload and try again.";
    }
    if (code === "invalid_events") {
      return INVALID_EVENTS_COPY;
    }
    if (code === "invalid_body") {
      return "Some config fields are invalid — check the tiers, success number, and webhook URL.";
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Save failed";
}

export default function LeadSalesWitzzy() {
  // Config plane (the witzzy_config singleton + the SEND tick cap).
  const [config, setConfig] = useState<WitzzyConfigResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Master on/off toggle state (drives setWitzzyEnabled — the SEND kill switch).
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [confirmingEnable, setConfirmingEnable] = useState(false);

  // Live plane (stats), windowed + polled.
  const [presetSec, setPresetSec] = useState<number>(TODAY_PT); // default: Today (PT)
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null);
  const [stats, setStats] = useState<WitzzyStats | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveLoading, setLiveLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [auto, setAuto] = useState(true);

  const statsOpts = useMemo(() => toStatsOpts(presetSec, custom), [presetSec, custom]);

  // --- Config load ---------------------------------------------------------
  const loadConfig = useCallback(() => {
    setConfigLoading(true);
    api
      .witzzyConfig()
      .then((r) => {
        if (!r || typeof r.config !== "object" || r.config === null) {
          throw new Error("Unexpected Witzzy config response — try reloading.");
        }
        setConfig(r);
        setForm(configToForm(r.config));
        setConfigError(null);
        setDirty(false);
      })
      .catch((e: unknown) =>
        setConfigError(e instanceof Error ? e.message : "Failed to load config"),
      )
      .finally(() => setConfigLoading(false));
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // --- Live load (stats) ----------------------------------------------------
  const load = useCallback(
    (showSpinner: boolean) => {
      if (showSpinner) setLiveLoading(true);
      api
        .witzzyStats(statsOpts)
        .then((s) => {
          setStats(s);
          setLiveError(null);
          setRefreshedAt(new Date());
        })
        .catch((err: unknown) => {
          setLiveError(err instanceof Error ? err.message : "Failed to load");
        })
        .finally(() => setLiveLoading(false));
    },
    [statsOpts],
  );

  // A ref so the poll timer always calls the latest fetch (current window)
  // without re-arming the interval on every filter change.
  const fetchRef = useRef<() => void>(() => {});
  useEffect(() => {
    fetchRef.current = () => load(false);
  }, [load]);

  // Re-fetch (with spinner) whenever the window changes.
  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-refresh polling — pauses when the tab is hidden or auto is toggled off.
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchRef.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [auto]);

  // --- Master on/off toggle (the SEND kill switch) --------------------------
  // Off is immediate; On requires the inline confirm (setConfirmingEnable) first.
  const setEnabled = useCallback((next: boolean) => {
    setToggling(true);
    setToggleError(null);
    api
      .setWitzzyEnabled(next)
      .then((cfg) => {
        setConfig((prev) =>
          prev ? { ...prev, config: cfg } : { config: cfg, tick_cap: TICK_CAP_FALLBACK },
        );
        setConfirmingEnable(false);
      })
      .catch((e: unknown) =>
        setToggleError(e instanceof Error ? e.message : "Failed to update"),
      )
      .finally(() => setToggling(false));
  }, []);

  // --- Config form -----------------------------------------------------------
  const update = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((f) => (f ? { ...f, [key]: value } : f));
      setDirty(true);
    },
    [],
  );

  const toggleTier = useCallback((tier: WitzzyTier) => {
    setForm((f) => {
      if (!f) return f;
      const has = f.tiers.includes(tier);
      const tiers = has ? f.tiers.filter((t) => t !== tier) : [...f.tiers, tier];
      return { ...f, tiers };
    });
    setDirty(true);
  }, []);

  // Send window (witzzy_send_window_quota): per-day open toggle + from/to
  // edits. Disabled from/to inputs can't be edited while closed, so a day's
  // strings are simply frozen (never cleared) until it's reopened.
  const toggleDayOpen = useCallback((day: WitzzyDayKey, open: boolean) => {
    setForm((f) => (f ? { ...f, days: { ...f.days, [day]: { ...f.days[day], open } } } : f));
    setDirty(true);
  }, []);

  const updateDay = useCallback((day: WitzzyDayKey, field: "from" | "to", value: string) => {
    setForm((f) =>
      f ? { ...f, days: { ...f.days, [day]: { ...f.days[day], [field]: value } } } : f,
    );
    setDirty(true);
  }, []);

  const tickCap = config?.tick_cap ?? TICK_CAP_FALLBACK;

  // Live client-side guards (mirror the server's 400 invalid_band): the band
  // (max_age > wait) and the conversion/fallback window PAIR. Only flag when
  // both parse — an empty field is caught by save() instead.
  const waitNum = form ? Number(form.waitMin) : NaN;
  const maxAgeNum = form ? Number(form.maxAgeMin) : NaN;
  const bandInvalid =
    Number.isFinite(waitNum) && Number.isFinite(maxAgeNum) && maxAgeNum <= waitNum;

  const conversionNum = form ? Number(form.conversionWindowMin) : NaN;
  const fallbackNum = form ? Number(form.fallbackMaxAgeMin) : NaN;
  const windowPairInvalid =
    Number.isFinite(conversionNum) &&
    Number.isFinite(fallbackNum) &&
    fallbackNum <= conversionNum + WITZZY_SYNC_GRACE_MIN;

  // Send window + daily quota live guards (mirror the server's 400
  // invalid_send_window / invalid_body) — computed from the same validators
  // buildConfigFromForm uses, so the two can't drift.
  const sendWindowError = form ? sendWindowFormError(form) : null;
  const sendWindowInvalid = sendWindowError !== null;
  const quotaError = form ? dailyQuotaFormError(form.dailyQuota) : null;
  const quotaInvalid = quotaError !== null;

  const save = useCallback(() => {
    if (!form || !config) return;
    const built = buildConfigFromForm(form, config.config.enabled, tickCap);
    if ("error" in built) {
      setConfigError(built.error);
      return;
    }
    setSaving(true);
    api
      .putWitzzyConfig(built.config)
      .then((cfg) => {
        setConfig((prev) =>
          prev ? { ...prev, config: cfg } : { config: cfg, tick_cap: TICK_CAP_FALLBACK },
        );
        setForm(configToForm(cfg));
        setConfigError(null);
        setDirty(false);
        setSavedAt(new Date());
      })
      .catch((e: unknown) => setConfigError(friendlyConfigError(e)))
      .finally(() => setSaving(false));
  }, [form, config, tickCap]);

  const enabled = config?.config.enabled ?? false;

  let healthHint: string;
  if (configLoading && !config) {
    healthHint = "Loading Witzzy status…";
  } else if (!enabled) {
    healthHint =
      "Witzzy is OFF — no new leads are posted (any lead already sent still resolves to Witzzy or LeadExec on schedule).";
  } else {
    healthHint = "Witzzy is ON — qualifying over-$10K leads are posted at the configured wait time.";
  }

  const presetLabel = custom
    ? "Custom range"
    : PRESETS.find((p) => p.sec === presetSec)?.label ?? "Window";

  const selectStyle: CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  const sends = stats?.sends;
  const outcomes = stats?.outcomes;
  const quota = stats?.quota;
  const tdPost = stats?.td_post;
  const events = stats?.events;

  return (
    <div>
      <PageHeader
        title="Outbound"
        subtitle="Outbound calling — Witzzy-first for over-$10K leads inside the send window, with a timed LeadExec fallback"
        actions={
          <div
            style={{
              display: "flex",
              gap: tokens.space.sm,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={custom ? "custom" : String(presetSec)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  const today = new Date().toISOString().slice(0, 10);
                  setCustom({ start: today, end: today });
                } else {
                  setCustom(null);
                  setPresetSec(Number(e.target.value));
                }
              }}
              style={selectStyle}
              aria-label="Time range"
            >
              {PRESETS.map((p) => (
                <option key={p.label} value={String(p.sec)}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom range…</option>
            </select>
            {custom && (
              <>
                <input
                  type="date"
                  value={custom.start}
                  max={custom.end}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={selectStyle}
                  aria-label="Start date"
                />
                <span style={{ color: tokens.color.fg2 }}>→</span>
                <input
                  type="date"
                  value={custom.end}
                  min={custom.start}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={selectStyle}
                  aria-label="End date"
                />
              </>
            )}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
              />
              Auto
            </label>
            <button
              type="button"
              onClick={() => load(true)}
              disabled={liveLoading}
              style={buttonStyle("ghost", liveLoading)}
            >
              {liveLoading ? "…" : "Refresh"}
            </button>
          </div>
        }
      />

      {refreshedAt && (
        <div
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginBottom: tokens.space.md,
          }}
        >
          Updated{" "}
          {refreshedAt.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
          {auto ? ` · auto-refresh ${POLL_MS / 1000}s` : ""}
          {" · "}
          {presetLabel}
        </div>
      )}

      {/* Master on/off toggle — the SEND kill switch, prominent, confirm-on-enable. */}
      <Card
        style={{
          marginBottom: tokens.space.xl,
          borderColor: enabled ? "rgba(0,212,170,0.28)" : tokens.color.border,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: tokens.space.lg,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md }}>
              <span
                style={{ fontWeight: 700, fontSize: tokens.font.size.lg, color: tokens.color.fg0 }}
              >
                Witzzy send
              </span>
              {configLoading && !config ? (
                <Pill tone="neutral">…</Pill>
              ) : enabled ? (
                <Pill tone="success">On</Pill>
              ) : (
                <Pill tone="neutral">Off</Pill>
              )}
            </div>
            <p style={{ margin: `${tokens.space.sm}px 0 0`, color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
              {healthHint}
            </p>
          </div>

          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
            {toggling ? (
              <button type="button" disabled style={buttonStyle("ghost", true)}>
                Working…
              </button>
            ) : enabled ? (
              <button
                type="button"
                onClick={() => setEnabled(false)}
                aria-label="Turn Witzzy off"
                style={buttonStyle("danger")}
              >
                Turn Off
              </button>
            ) : confirmingEnable ? (
              <>
                <button
                  type="button"
                  onClick={() => setEnabled(true)}
                  aria-label="Confirm — start posting live leads to Witzzy"
                  style={buttonStyle("primary")}
                >
                  Yes, start posting live
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingEnable(false)}
                  style={buttonStyle("ghost")}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingEnable(true)}
                aria-label="Turn Witzzy on"
                disabled={configLoading}
                style={buttonStyle("primary", configLoading)}
              >
                Turn On
              </button>
            )}
          </div>
        </div>

        {confirmingEnable && !enabled && (
          <div
            role="alert"
            style={{
              marginTop: tokens.space.lg,
              padding: `${tokens.space.md}px ${tokens.space.lg}px`,
              borderRadius: tokens.radius.md,
              background: tokens.color.dangerBg,
              border: `1px solid ${tokens.color.dangerBorder}`,
              color: tokens.color.danger,
              fontSize: tokens.font.size.sm,
            }}
          >
            <strong>Are you sure — this starts posting live leads to Witzzy.</strong>{" "}
            Turning sends on posts qualifying over-$10K leads to your configured
            webhook on the next tick (real leads, real contact). Leads Witzzy
            doesn&apos;t convert within the window still fall back to LeadExec as
            today. Off is immediate; on is deliberate.
          </div>
        )}

        {toggleError && (
          <div role="alert" style={{ marginTop: tokens.space.md, color: tokens.color.danger, fontSize: tokens.font.size.sm }}>
            {toggleError}
          </div>
        )}
      </Card>

      {liveError && !stats && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <EmptyState title="Failed to load stats" hint={liveError} />
        </Card>
      )}

      {/* Stats strip — sends (witzzy.send) + resolutions (witzzy.resolve). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        {liveLoading && !stats ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : (
          <>
            {/* ALWAYS today-scoped in the configured zone, regardless of the
                range picker above (witzzy_send_window_quota; the hint says
                so explicitly). */}
            <StatCard
              label="Sent today"
              value={
                quota
                  ? `${fmtCount(quota.sent_today)} / ${quota.daily_quota === null ? "∞" : fmtCount(quota.daily_quota)}`
                  : "—"
              }
              hint={quota ? quotaHint(quota) : ""}
              emphasis
            />
            <StatCard label="Sent" value={fmtCount(sends?.["accepted"] ?? 0)} hint="accepted by Witzzy" />
            <StatCard label="Unconfirmed" value={fmtCount(sends?.["unconfirmed"] ?? 0)} hint="timeout/network — may still be received" />
            <StatCard
              label="Converted"
              value={fmtCount(events?.converted ?? 0)}
              hint="called the Witzzy number within 7 days of the send (call events, since 2026-09-11)"
            />
            <StatCard
              label="Converted before fallback"
              value={fmtCount(outcomes?.["converted"] ?? 0)}
              hint="called inside the LeadExec window — the ledger's decision"
            />
            <StatCard label="Fallback sold" value={fmtCount(outcomes?.["fallback_sold"] ?? 0)} hint="sold to LeadExec after the window" />
            <StatCard label="Expired" value={fmtCount(outcomes?.["expired"] ?? 0)} hint="still pending past fallback max age" />
            <StatCard label="Blocked" value={fmtCount(outcomes?.["blocked_exclusive"] ?? 0)} hint="withheld by an exclusive buyer" />
            <StatCard label="Failed" value={fmtCount(sends?.["failed"] ?? 0)} hint="send transport failure" />
            <StatCard
              label="TD has lead"
              value={fmtCount(
                (tdPost?.["posted"] ?? 0) +
                  (tdPost?.["existing_lead"] ?? 0) +
                  (tdPost?.["rejected_dupe"] ?? 0),
              )}
              hint="posted, or already known to TrackDrive"
            />
            <StatCard label="TD DNC" value={fmtCount(tdPost?.["rejected_dnc"] ?? 0)} hint="withheld from Witzzy" />
            <StatCard
              label="TD failed"
              value={fmtCount(
                (tdPost?.["rejected_other"] ?? 0) +
                  (tdPost?.["failed"] ?? 0) +
                  (tdPost?.["invalid_phone"] ?? 0),
              )}
              hint="TrackDrive post failed"
            />
            <StatCard
              label="Events sent"
              value={fmtCount(
                (events?.sent?.["call_received_target"] ?? 0) +
                  (events?.sent?.["call_received_other"] ?? 0),
              )}
              hint={`target ${fmtCount(events?.sent?.["call_received_target"] ?? 0)} · other ${fmtCount(events?.sent?.["call_received_other"] ?? 0)}`}
            />
            <StatCard
              label="Events failed"
              value={fmtCount(
                (events?.status?.["failed"] ?? 0) +
                  (events?.status?.["failed_4xx"] ?? 0) +
                  (events?.status?.["release_failed"] ?? 0),
              )}
              hint="Witzzy did not accept the event"
            />
          </>
        )}
      </div>

      {/* Config form */}
      <Card title="Configuration" style={{ marginBottom: tokens.space.xl }}>
        {configLoading && !form ? (
          <Skeleton height={280} />
        ) : configError && !form ? (
          <EmptyState title="Failed to load config" hint={configError} />
        ) : form ? (
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.lg }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <FieldLabel htmlFor="wz-wait-min" hint="minutes after the form-fill before a lead is eligible for the send pass">
                  Wait (minutes)
                </FieldLabel>
                <input
                  id="wz-wait-min"
                  type="number"
                  min="1"
                  step="1"
                  value={form.waitMin}
                  onChange={(e) => update("waitMin", e.target.value)}
                  style={inputStyle}
                  aria-label="Wait (minutes)"
                />
              </div>
              <div>
                <FieldLabel htmlFor="wz-max-age-min" hint="minutes after the form-fill past which a lead is too stale for the send pass">
                  Max age (minutes)
                </FieldLabel>
                <input
                  id="wz-max-age-min"
                  type="number"
                  min="1"
                  step="1"
                  value={form.maxAgeMin}
                  onChange={(e) => update("maxAgeMin", e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: bandInvalid ? tokens.color.dangerBorder : tokens.color.borderMid,
                  }}
                  aria-label="Max age (minutes)"
                />
              </div>
              <div>
                <FieldLabel htmlFor="wz-sends-per-min" hint={`sends/min, capped at ${tickCap}`}>
                  Sends per minute
                </FieldLabel>
                <input
                  id="wz-sends-per-min"
                  type="number"
                  min="1"
                  max={tickCap}
                  step="1"
                  value={form.sendsPerMin}
                  onChange={(e) => update("sendsPerMin", e.target.value)}
                  style={inputStyle}
                  aria-label="Sends per minute"
                />
              </div>
            </div>
            {bandInvalid && (
              <div role="alert" style={{ color: tokens.color.danger, fontSize: tokens.font.size.xs }}>
                Max age must be greater than wait — the send-pass band is empty as entered.
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <FieldLabel htmlFor="wz-conversion-window" hint="how long Witzzy has to convert a lead (a call to the success number) before the fallback considers it">
                  Conversion window — X (minutes, 1–720)
                </FieldLabel>
                <input
                  id="wz-conversion-window"
                  type="number"
                  min="1"
                  max="720"
                  step="1"
                  value={form.conversionWindowMin}
                  onChange={(e) => update("conversionWindowMin", e.target.value)}
                  style={inputStyle}
                  aria-label="Conversion window (minutes)"
                />
              </div>
              <div>
                <FieldLabel
                  htmlFor="wz-fallback-max-age"
                  hint={`must exceed the conversion window by more than ${WITZZY_SYNC_GRACE_MIN} minutes (2–1080)`}
                >
                  Fallback max age (minutes)
                </FieldLabel>
                <input
                  id="wz-fallback-max-age"
                  type="number"
                  min="2"
                  max="1080"
                  step="1"
                  value={form.fallbackMaxAgeMin}
                  onChange={(e) => update("fallbackMaxAgeMin", e.target.value)}
                  style={{
                    ...inputStyle,
                    borderColor: windowPairInvalid ? tokens.color.dangerBorder : tokens.color.borderMid,
                  }}
                  aria-label="Fallback max age (minutes)"
                />
              </div>
            </div>
            {windowPairInvalid && (
              <div role="alert" style={{ color: tokens.color.danger, fontSize: tokens.font.size.xs }}>
                Fallback max age must exceed the conversion window by more than{" "}
                {WITZZY_SYNC_GRACE_MIN} minutes (the td-calls sync grace) — as entered, a
                pending lead could expire before a late-window call is even visible.
              </div>
            )}

            {/* Send window (witzzy_send_window_quota): the send pass only
                runs inside this local operating window. */}
            <div>
              <FieldLabel hint="the send pass only runs inside this local operating window; IANA zone, e.g. America/Los_Angeles">
                Send window
              </FieldLabel>
              <input
                id="wz-tz"
                type="text"
                value={form.tz}
                onChange={(e) => update("tz", e.target.value)}
                style={{
                  ...inputStyle,
                  maxWidth: 320,
                  borderColor:
                    form.tz.trim() === "" ? tokens.color.dangerBorder : tokens.color.borderMid,
                }}
                aria-label="Time zone"
              />
              <div
                style={{
                  marginTop: tokens.space.md,
                  display: "flex",
                  flexDirection: "column",
                  gap: tokens.space.sm,
                }}
              >
                {WITZZY_DAY_KEYS.map((d) => {
                  const row = form.days[d];
                  return (
                    <div
                      key={d}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: tokens.space.md,
                        flexWrap: "wrap",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          minWidth: 130,
                          color: tokens.color.fg0,
                          fontSize: tokens.font.size.sm,
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={row.open}
                          onChange={(e) => toggleDayOpen(d, e.target.checked)}
                          aria-label={`${DAY_LABELS[d]} open`}
                        />
                        {DAY_LABELS[d]}
                      </label>
                      <input
                        type="time"
                        value={row.from}
                        disabled={!row.open}
                        onChange={(e) => updateDay(d, "from", e.target.value)}
                        style={{ ...inputStyle, width: 130 }}
                        aria-label={`${DAY_LABELS[d]} opens`}
                      />
                      <span style={{ color: tokens.color.fg2 }}>–</span>
                      <input
                        type="time"
                        value={row.to}
                        disabled={!row.open}
                        onChange={(e) => updateDay(d, "to", e.target.value)}
                        style={{ ...inputStyle, width: 130 }}
                        aria-label={`${DAY_LABELS[d]} closes`}
                      />
                    </div>
                  );
                })}
              </div>
              {sendWindowError && (
                <div
                  role="alert"
                  style={{ marginTop: tokens.space.xs, color: tokens.color.danger, fontSize: tokens.font.size.xs }}
                >
                  {sendWindowError}
                </div>
              )}
            </div>

            <div>
              <FieldLabel
                htmlFor="wz-daily-quota"
                hint="leave blank for unlimited — counts leads handed to Witzzy per local day"
              >
                Daily quota
              </FieldLabel>
              <input
                id="wz-daily-quota"
                type="number"
                min="1"
                step="1"
                value={form.dailyQuota}
                onChange={(e) => update("dailyQuota", e.target.value)}
                style={{
                  ...inputStyle,
                  maxWidth: 200,
                  borderColor: quotaError ? tokens.color.dangerBorder : tokens.color.borderMid,
                }}
                aria-label="Daily quota"
              />
              {quotaError && (
                <div
                  role="alert"
                  style={{ marginTop: tokens.space.xs, color: tokens.color.danger, fontSize: tokens.font.size.xs }}
                >
                  {quotaError}
                </div>
              )}
            </div>

            <div>
              <FieldLabel hint="Witzzy only sees leads whose route tier is checked below (at least one required)">
                Tiers Witzzy covers
              </FieldLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: tokens.space.md }}>
                {TIER_OPTIONS.map((t) => (
                  <label
                    key={t.value}
                    style={{ display: "flex", alignItems: "center", gap: 6, color: tokens.color.fg0, fontSize: tokens.font.size.sm, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={form.tiers.includes(t.value)}
                      onChange={() => toggleTier(t.value)}
                      aria-label={t.label}
                    />
                    {t.label}
                  </label>
                ))}
              </div>
              {form.tiers.length === 0 && (
                <div role="alert" style={{ marginTop: tokens.space.xs, color: tokens.color.danger, fontSize: tokens.font.size.xs }}>
                  Pick at least one tier.
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <FieldLabel htmlFor="wz-dest-name">Destination name</FieldLabel>
                <input
                  id="wz-dest-name"
                  type="text"
                  value={form.destinationName}
                  onChange={(e) => update("destinationName", e.target.value)}
                  style={inputStyle}
                  aria-label="Destination name"
                />
              </div>
              <div>
                <FieldLabel
                  htmlFor="wz-webhook-url"
                  hint="stored securely; only a masked hint is shown. Paste to set / replace — leave blank to keep the current URL"
                >
                  Webhook URL
                </FieldLabel>
                <div style={{ marginBottom: tokens.space.xs, color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
                  Current URL:{" "}
                  <span style={{ fontFamily: tokens.font.mono }}>
                    {config?.config.destination.webhook_url_hint ?? "not set"}
                  </span>
                </div>
                <input
                  id="wz-webhook-url"
                  type="text"
                  placeholder="paste to set / replace"
                  value={form.webhookUrlNew}
                  onChange={(e) => update("webhookUrlNew", e.target.value)}
                  style={inputStyle}
                  aria-label="Webhook URL"
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: tokens.color.fg0,
                    fontSize: tokens.font.size.sm,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.tdLeadPostEnabled}
                    onChange={(e) => update("tdLeadPostEnabled", e.target.checked)}
                    aria-label="Post leads to TrackDrive"
                  />
                  Post leads to TrackDrive
                </label>
              </div>
              <div>
                <FieldLabel
                  htmlFor="wz-td-lead-token"
                  hint="stored securely; only a masked hint is shown. Paste to set / replace — leave blank to keep the current token. Before pasting, confirm in TrackDrive that the X-Factor (Internal) traffic source has NO callback/dial schedule — a posted lead must not make TrackDrive dial the consumer. A replaced token takes effect on the Lambda's next cold start."
                >
                  TrackDrive lead token
                </FieldLabel>
                <div style={{ marginBottom: tokens.space.xs, color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
                  Current token:{" "}
                  <span style={{ fontFamily: tokens.font.mono }}>
                    {config?.config.destination.td_lead_token_hint ?? "not set"}
                  </span>
                </div>
                <input
                  id="wz-td-lead-token"
                  type="text"
                  placeholder="paste to set / replace"
                  value={form.tdLeadTokenNew}
                  onChange={(e) => update("tdLeadTokenNew", e.target.value)}
                  style={inputStyle}
                  aria-label="TrackDrive lead token"
                />
              </div>
            </div>

            {/* witzzy_call_events — placed directly under the TrackDrive
                lead-post block per the PRP (AC11 page half). */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: tokens.color.fg0,
                    fontSize: tokens.font.size.sm,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.eventsEnabled}
                    onChange={(e) => update("eventsEnabled", e.target.checked)}
                    aria-label="Send call events to Witzzy"
                  />
                  Send call events to Witzzy
                </label>
              </div>
              <div>
                <FieldLabel
                  htmlFor="wz-attribution-days"
                  hint="calls inside this many days after the send are reported to Witzzy — 1 to 30"
                >
                  Attribution window (days)
                </FieldLabel>
                <input
                  id="wz-attribution-days"
                  type="number"
                  min="1"
                  max="30"
                  step="1"
                  value={form.attributionWindowDays}
                  onChange={(e) => update("attributionWindowDays", e.target.value)}
                  style={{ ...inputStyle, maxWidth: 200 }}
                  aria-label="Attribution window (days)"
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <FieldLabel
                  htmlFor="wz-events-url"
                  hint="stored securely; only a masked hint is shown. Paste to set / replace — leave blank to keep the current URL. Events start with calls synced after the switch is on (no backfill)."
                >
                  Events webhook URL
                </FieldLabel>
                <div style={{ marginBottom: tokens.space.xs, color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
                  Current events URL:{" "}
                  <span style={{ fontFamily: tokens.font.mono }}>
                    {config?.config.destination.events_url_hint ?? "not set"}
                  </span>
                </div>
                <input
                  id="wz-events-url"
                  type="text"
                  placeholder="paste to set / replace"
                  value={form.eventsUrlNew}
                  onChange={(e) => update("eventsUrlNew", e.target.value)}
                  style={inputStyle}
                  aria-label="Events webhook URL"
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: tokens.space.lg }}>
              <div>
                <FieldLabel htmlFor="wz-success-number" hint="the 10-digit dialled number that counts as a Witzzy conversion">
                  Success number (10 digits)
                </FieldLabel>
                <input
                  id="wz-success-number"
                  type="text"
                  value={form.successNumber10}
                  onChange={(e) => update("successNumber10", e.target.value)}
                  style={inputStyle}
                  aria-label="Success number"
                />
              </div>
              <div>
                <FieldLabel htmlFor="wz-traffic-source" hint="dashboard corroboration only — NOT read by the conversion check; leave blank for none">
                  Traffic source id
                </FieldLabel>
                <input
                  id="wz-traffic-source"
                  type="text"
                  value={form.successTrafficSourceId}
                  onChange={(e) => update("successTrafficSourceId", e.target.value)}
                  style={inputStyle}
                  aria-label="Traffic source id"
                />
              </div>
            </div>

            {configError && (
              <div role="alert" style={{ color: tokens.color.danger, fontSize: tokens.font.size.sm }}>
                {configError}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md }}>
              <button
                type="button"
                onClick={save}
                disabled={
                  !dirty ||
                  saving ||
                  bandInvalid ||
                  windowPairInvalid ||
                  form.tiers.length === 0 ||
                  sendWindowInvalid ||
                  quotaInvalid
                }
                style={buttonStyle(
                  "primary",
                  !dirty ||
                    saving ||
                    bandInvalid ||
                    windowPairInvalid ||
                    form.tiers.length === 0 ||
                    sendWindowInvalid ||
                    quotaInvalid,
                )}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {savedAt && !dirty && (
                <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
                  Saved{" "}
                  {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              )}
            </div>
          </div>
        ) : (
          <EmptyState title="No config loaded" />
        )}
      </Card>
    </div>
  );
}

function SkeletonStat() {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
      }}
    >
      <Skeleton height={10} width="40%" />
      <div style={{ height: tokens.space.sm }} />
      <Skeleton height={28} width="70%" />
      <div style={{ height: tokens.space.xs }} />
      <Skeleton height={10} width="50%" />
    </div>
  );
}
